/**
 * Per-stage API + auth configuration.
 *
 * Pure TS — no React. The base URL is the API Gateway invoke URL for the
 * EngineeringVector Core Service in the corresponding stage/region. The Cognito
 * block describes the hosted-UI OAuth2 (Authorization Code + PKCE) parameters
 * used by {@link CognitoTokenProvider}.
 *
 * Values here are the REAL Prod (us-east-1) deployment. They remain
 * stage-aware/overridable so Beta or a future stage can supply its own invoke
 * URL / app client without touching call sites.
 */

export type Stage = 'Prod' | 'Beta' | 'Local';

export interface ApiConfig {
  /** Absolute base URL of the API Gateway stage, no trailing slash. */
  baseUrl: string;
}

/** Real Prod (us-east-1) API Gateway invoke URL. */
const PROD_BASE_URL =
  'https://1nhvj7trs0.execute-api.us-east-1.amazonaws.com/Prod';

const STAGE_BASE_URLS: Record<Stage, string> = {
  Prod: PROD_BASE_URL,
  // TODO: inject the real Beta invoke URL when a Beta stage exists.
  Beta: 'https://REPLACE_ME.execute-api.us-east-1.amazonaws.com/Beta',
  // Local dev still calls the REAL Prod backend by default (see DEFAULT_STAGE);
  // this entry only applies if a caller explicitly requests the 'Local' stage.
  Local: PROD_BASE_URL,
};

const DEFAULT_STAGE: Stage = 'Prod';

/**
 * Returns the API configuration for a stage. Defaults to Prod.
 */
export function getApiConfig(stage: Stage = DEFAULT_STAGE): ApiConfig {
  const baseUrl = STAGE_BASE_URLS[stage];
  return { baseUrl: baseUrl.replace(/\/+$/, '') };
}

/**
 * Cognito hosted-UI OAuth2 configuration for a stage.
 *
 * The app is a PUBLIC OAuth client (no secret) that uses the Authorization Code
 * + PKCE flow against the AmazonFederate (Midway) hosted UI. `redirectUri` /
 * `logoutUri` are derived from the current window origin so the SAME app client
 * works for local dev (http://localhost:3000, the registered callback) and any
 * deployed origin — PROVIDED that origin's callback/logout URLs are also
 * whitelisted on the app client (see README note in vite.config.ts).
 */
export interface CognitoConfig {
  region: string;
  userPoolId: string;
  clientId: string;
  /** Hosted-UI domain, no trailing slash. */
  hostedUiDomain: string;
  /** OAuth scopes requested at authorize time. */
  scopes: string[];
  /** Redirect URI registered on the app client (this origin's callback route). */
  redirectUri: string;
  /** Post-logout redirect URI registered on the app client. */
  logoutUri: string;
  /** OAuth response type. Authorization Code flow => 'code'. */
  responseType: 'code';
  /** Federated IdP alias to jump straight to (Midway). */
  identityProvider: string;
}

/** Safe origin lookup — falls back to the registered localhost dev origin. */
function currentOrigin(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return 'http://localhost:3000';
}

/** Real Prod (us-east-1) Cognito pool + hosted-UI values. */
const PROD_COGNITO = {
  region: 'us-east-1',
  userPoolId: 'us-east-1_tYNWvt35a',
  clientId: '2hdj5cqk5e4hbq19l6kgg1ehcq',
  hostedUiDomain:
    'https://perfect-universe-portal-user-pool-domain-prod-us-east-1.auth.us-east-1.amazoncognito.com',
  scopes: ['openid', 'email', 'profile'],
  responseType: 'code' as const,
  identityProvider: 'AmazonFederate',
};

/**
 * Returns the Cognito config for a stage. Defaults to Prod. `redirectUri` /
 * `logoutUri` are computed from the live origin at call time.
 */
export function getCognitoConfig(_stage: Stage = DEFAULT_STAGE): CognitoConfig {
  const origin = currentOrigin();
  return {
    ...PROD_COGNITO,
    hostedUiDomain: PROD_COGNITO.hostedUiDomain.replace(/\/+$/, ''),
    redirectUri: `${origin}/global/auth/callback`,
    logoutUri: `${origin}/global/auth/logout`,
  };
}
